import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge, PaymentBadge } from "@/components/StatusBadge";
import { ReportDialog } from "@/components/ReportDialog";
import { Avatar } from "@/components/CompanionCard";
import { api, errMsg, inr, fmtDay, fmtDateTime, getPublicSettings } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { AccountPanel } from "@/components/AccountPanel";

const refundEstimate = (b, s) => {
  if (!["collected", "confirmed"].includes(b.payment_status)) return { unpaid: true };
  if (!s) return null;
  const hrs = (new Date(b.start_at) - new Date()) / 36e5;
  const pct = hrs >= s.full_refund_hours ? 100 : hrs >= s.partial_refund_hours ? s.partial_refund_pct : 0;
  return { pct, amount: Math.round(b.pricing.total * pct) / 100 };
};

const CancelDialog = ({ b, onDone }) => {
  const [reason, setReason] = useState("");
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (open) getPublicSettings().then(setS);
  }, [open]);
  const est = refundEstimate(b, s);
  const go = async () => {
    try {
      const { data } = await api.post(`/bookings/${b.id}/cancel`, { reason });
      toast.success(`Cancelled. Refund: ${inr(data.refund_amount)}`);
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full" data-testid={`cancel-booking-btn-${b.ref}`}>Cancel</Button></DialogTrigger>
      <DialogContent data-testid="cancel-dialog">
        <DialogHeader><DialogTitle>Cancel {b.ref}?</DialogTitle>
          <DialogDescription>Per our <Link to="/refund-policy" className="underline">cancellation policy</Link>: estimated refund <span data-testid="cancel-refund-estimate">{!est ? "calculating…" : est.unpaid ? "₹0 (no cash collected)" : `${est.pct}% = ${inr(est.amount)}`}</span>.</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="cancel-reason-input" placeholder="Reason (min 3 characters)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <Button data-testid="cancel-confirm-btn" variant="destructive" disabled={reason.trim().length < 3} onClick={go}>Confirm cancellation</Button>
      </DialogContent>
    </Dialog>
  );
};

const ReviewDialog = ({ b, onDone }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);
  const go = async () => {
    try {
      await api.post(`/bookings/${b.id}/review`, { rating, comment });
      toast.success("Thanks for your review!");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="rounded-full" data-testid={`review-booking-btn-${b.ref}`}>Review</Button></DialogTrigger>
      <DialogContent data-testid="review-dialog">
        <DialogHeader><DialogTitle>Rate {b.companion?.display_name}</DialogTitle></DialogHeader>
        <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}><Star className={n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} /></button>)}</div>
        <Textarea data-testid="review-comment-input" placeholder="Share your experience (min 5 characters)" value={comment} onChange={(e) => setComment(e.target.value)} />
        <Button data-testid="review-submit-btn" disabled={comment.trim().length < 5} onClick={go}>Submit review</Button>
      </DialogContent>
    </Dialog>
  );
};

const BookingRow = ({ b, reload, acts }) => (
  <div className="card-surface p-5 flex flex-col sm:flex-row gap-4 sm:items-center" data-testid={`booking-row-${b.ref}`}>
    {b.companion && <Avatar c={b.companion} className="w-16 h-16 rounded-xl shrink-0" />}
    <div className="flex-1 space-y-1.5 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-semibold" data-testid="booking-ref">{b.ref}</span>
        <StatusBadge status={b.status} /><PaymentBadge status={b.payment_status} />{b.is_demo && <span className="demo-badge">DEMO</span>}
      </div>
      <p className="text-sm text-slate-700"><b>{b.companion?.display_name}</b> · {acts[b.activity] || b.activity} · {fmtDay(b.date)} {b.start_time} IST · {b.duration_hours}h</p>
      <p className="text-xs text-slate-500">Total {inr(b.pricing.total)}{b.refunded_amount > 0 && ` · Refunded ${inr(b.refunded_amount)}`}{b.cancellation && ` · Cancelled by ${b.cancellation.by}`}{b.cancellation?.refund_status === "pending_retry" && <span className="text-amber-700 font-semibold" data-testid="refund-pending-note"> · Refund {inr(b.cancellation.refund_due)} pending</span>}</p>
    </div>
    <div className="flex flex-wrap gap-2">
      <Button asChild size="sm" variant="ghost" className="rounded-full" data-testid={`view-booking-btn-${b.ref}`}><Link to={`/checkout/${b.id}`}>View details</Link></Button>
      {["pending", "accepted", "confirmed"].includes(b.status) && <CancelDialog b={b} onDone={reload} />}
      {b.status === "completed" && !b.reviewed && <ReviewDialog b={b} onDone={reload} />}
      {["confirmed", "completed"].includes(b.status) && <ReportDialog type="dispute" bookingId={b.id} title="Raise a dispute / complaint" trigger={<Button size="sm" variant="ghost" className="rounded-full" data-testid={`dispute-booking-btn-${b.ref}`}>Dispute</Button>} />}
    </div>
  </div>
);

const Tickets = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/tickets/mine").then((r) => setItems(r.data));
  }, []);
  if (!items.length) return <p className="text-sm text-slate-500 py-8">No reports or tickets yet.</p>;
  return <div className="space-y-3">{items.map((t) => <div key={t.id} className="card-surface p-4 text-sm" data-testid="my-ticket-row"><div className="flex justify-between"><b>{t.subject}</b><span className="chip">{t.status}</span></div>{t.admin_note && <p className="text-slate-600 mt-1">Support: {t.admin_note}</p>}<p className="text-xs text-slate-400 mt-1">{fmtDateTime(t.created_at)}</p></div>)}</div>;
};

const Blocked = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/blocks").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  if (!items.length) return <p className="text-sm text-slate-500 py-8">You haven't blocked anyone.</p>;
  return <div className="space-y-2">{items.map((b) => <div key={b.companion_id} className="card-surface p-4 flex justify-between items-center text-sm"><span>{b.display_name}</span><Button size="sm" variant="outline" data-testid={`unblock-btn-${b.companion_id}`} onClick={() => api.delete(`/blocks/${b.companion_id}`).then(load)}>Unblock</Button></div>)}</div>;
};

export default function Dashboard() {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [acts, setActs] = useState({});
  const [params] = useSearchParams();
  const load = () => api.get("/bookings/mine").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/activities").then((r) => setActs(Object.fromEntries(r.data.map((a) => [a.slug, lang === "hi" ? a.name_hi : a.name_en]))));
  }, [lang]);

  return (
    <div className="container-x w-full min-w-0 py-10 space-y-6" data-testid="customer-dashboard">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div><p className="eyebrow">Customer</p><h1 className="text-4xl sm:text-5xl font-bold mt-1">{t("my_bookings")}</h1></div>
        <Button asChild className="rounded-full" data-testid="dashboard-find-btn"><Link to="/companions">{t("nav_find")}</Link></Button>
      </div>
      <Tabs defaultValue={params.get("tab") || "bookings"} key={params.get("tab") || "bookings"}>
        <TabsList><TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger><TabsTrigger value="tickets" data-testid="tab-tickets">Reports</TabsTrigger><TabsTrigger value="blocked" data-testid="tab-blocked">Blocked</TabsTrigger><TabsTrigger value="account" data-testid="tab-account">Account</TabsTrigger></TabsList>
        <TabsContent value="bookings" className="space-y-3 mt-4">
          {items === null ? <Loader2 className="animate-spin" /> : items.length === 0 ? <p className="text-slate-500 py-8" data-testid="no-bookings">No bookings yet.</p> : items.map((b) => <BookingRow key={b.id} b={b} reload={load} acts={acts} />)}
        </TabsContent>
        <TabsContent value="tickets" className="mt-4"><Tickets /></TabsContent>
        <TabsContent value="blocked" className="mt-4"><Blocked /></TabsContent>
        <TabsContent value="account" className="mt-4"><AccountPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
